import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const TestMultiSelect = ({ tests, selected, onChange, testid }) => {
  const [q, setQ] = useState("");
  const filtered = tests.filter((t) => `${t.name} ${t.code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid={testid}>
      <input className={inputCls} placeholder="Search tests to add…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
      <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y">
        {filtered.map((t) => (
          <label key={t.test_id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-opt-${t.test_id}`}>
            <input type="checkbox" checked={selected.includes(t.test_id)} onChange={() => toggle(t.test_id)} />
            <span className="text-slate-800">{t.name}</span><span className="text-xs text-slate-400">{t.code}</span>
            <span className="ml-auto text-xs text-slate-500">₹{Number(t.price).toLocaleString("en-IN")}</span>
          </label>
        ))}
        {!filtered.length && <p className="px-3 py-4 text-xs text-slate-400">No tests found.</p>}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">{selected.length} selected</p>
    </div>
  );
};

export default function LabPanels() {
  const { can } = useAuth();
  const manage = can("lab.tests.manage");
  const [items, setItems] = useState(null);
  const [tests, setTests] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", test_ids: [], price: "", active: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [{ data: p }, { data: t }] = await Promise.all([api.get("/lab/panels"), api.get("/lab/tests", { params: { active: true } })]);
      setItems(p.items); setTests(t.items);
    } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", category: "", test_ids: [], price: "", active: true }); setFormError(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, category: p.category || "", test_ids: p.test_ids || [], price: p.price ? String(p.price) : "", active: p.active }); setFormError(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Panel name is required."); return; }
    if (!form.test_ids.length) { setFormError("Add at least one test to the panel."); return; }
    setBusy(true); setFormError("");
    try {
      const payload = { name: form.name, category: form.category, test_ids: form.test_ids, price: Number(form.price) || 0, active: form.active };
      if (editing) await api.patch(`/lab/panels/${editing.panel_id}`, payload);
      else await api.post("/lab/panels", payload);
      toast.success(editing ? "Panel updated." : "Panel added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (p) => { try { await api.patch(`/lab/panels/${p.panel_id}`, { active: !p.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.tests.view")) return <NoAccess what="Lab test panels" />;

  return (
    <div data-testid="lab-panels-page">
      <PageHeader overline="Laboratory" title="Test Panels"
        subtitle="Group related tests (e.g. LFT, KFT) into a panel with an optional fixed price."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-panel-btn"><Plus className="h-4 w-4" /> Add panel</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-panels-table" columns={["Panel", "Category", "Tests", "Price (₹)", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No panels yet" subtitle="Create your first test panel." testid="lab-panels-empty" />}
          renderRow={(p) => (
            <tr key={p.panel_id} data-testid={`lab-panel-row-${p.panel_id}`}>
              <Td className="font-medium text-slate-900">{p.name}</Td>
              <Td className="text-slate-600">{p.category || "—"}</Td>
              <Td className="text-slate-600">{(p.tests || []).length} tests</Td>
              <Td className="text-slate-700">{p.price > 0 ? Number(p.price).toLocaleString("en-IN") : `${Number(p.derived_price).toLocaleString("en-IN")} (derived)`}</Td>
              <Td><span className={`text-xs font-medium ${p.active ? "text-emerald-600" : "text-slate-400"}`}>{p.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className={ghostBtnCls} data-testid={`edit-panel-${p.panel_id}`}>Edit</button>
                  <button onClick={() => toggle(p)} className={ghostBtnCls} data-testid={`toggle-panel-${p.panel_id}`}>{p.active ? "Archive" : "Restore"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit panel" : "Add panel"} wide testid="lab-panel-modal">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Panel name *"><input className={inputCls} data-testid="panel-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Category"><input className={inputCls} data-testid="panel-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Fixed price (₹, 0 = derive)"><input type="number" min="0" className={inputCls} data-testid="panel-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
          </div>
          <Field label="Member tests *"><TestMultiSelect tests={tests} selected={form.test_ids} onChange={(ids) => setForm({ ...form, test_ids: ids })} testid="panel-tests" /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="panel-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="panel-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-panel-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
